import { HOME_MESSENGER_MENU_ITEMS, WIDGET_HOME_LIMITS, consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Card, CardBody, CardHeader, CardTitle, Field, Icon, StatusBadge, Switch, Textarea, type StatusKind } from "@sagot/ui";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { formatDate } from "../shared/format";
import type { MessengerProfile, MessengerSyncStatus } from "./home-model";
import type { WidgetForm } from "./widget-model";

const K = "deployKnowledge.widgetStudio.home.messenger";
/** Shown literally: Facebook replaces it with the visitor's first name. */
const FIRST_NAME_TOKEN = "{{user_first_name}}";
const SYNC_KIND: Record<MessengerSyncStatus | "never", StatusKind> = { synced: "success", removed: "neutral", failed: "danger", skipped: "neutral", never: "neutral" };

/** What was last pushed to the connected Page (icon + text + colour); a note to connect a Page when there is none. */
function SyncStatus({ profile }: { profile: MessengerProfile | null }) {
  const { t, i18n } = useTranslation();
  if (!profile) {
    return (
      <p data-testid="messenger-no-page" className="text-xs text-fg-subtle">
        {t(`${K}.noPage`)}
      </p>
    );
  }
  const state = profile.status ?? "never";
  return (
    <div data-testid="messenger-sync" data-sync={state} className="space-y-1.5 rounded-lg border border-line p-3 text-xs">
      <p className="font-semibold">{t(`${K}.syncPage`, { page: profile.pageName })}</p>
      <StatusBadge status={SYNC_KIND[state]} label={t(`${K}.sync.${state}`)} />
      {profile.error ? <p className="text-fg-muted">{profile.error}</p> : null}
      {profile.syncedAt ? <p className="text-fg-subtle">{t(`${K}.syncedAt`, { when: formatDate(profile.syncedAt, i18n.language, true) })}</p> : null}
    </div>
  );
}

/** Messenger Get Started menu: the greeting, the Get Started button and the persistent menu (WF-P2-19 syncs it to the Page). */
export function HomeMessenger({ agentId, profile }: { agentId: string; profile: MessengerProfile | null }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const { control, register } = useFormContext<WidgetForm>();
  const greeting = useWatch({ control, name: "home.messenger.greeting" });
  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>{t(`${K}.title`)}</CardTitle>
          <p className="text-xs text-fg-subtle">{t(`${K}.hint`)}</p>
        </div>
        <Controller control={control} name="home.messenger.get_started" render={({ field }) => <Switch checked={field.value} onCheckedChange={field.onChange} aria-label={t(`${K}.enable`)} />} />
      </CardHeader>
      <CardBody className="space-y-4">
        <Field
          label={t(`${K}.greeting`)}
          hint={
            <>
              {t(`${K}.greetingHint`, { max: WIDGET_HOME_LIMITS.messengerGreeting, token: FIRST_NAME_TOKEN })}{" "}
              <span className="font-mono">{t("deployKnowledge.widgetStudio.home.screen.counter", { count: greeting.length, max: WIDGET_HOME_LIMITS.messengerGreeting })}</span>
            </>
          }
        >
          <Textarea rows={2} maxLength={WIDGET_HOME_LIMITS.messengerGreeting} {...register("home.messenger.greeting")} />
        </Field>
        <div className="flex items-start justify-between gap-4 rounded-lg border border-line p-4">
          <div>
            <p className="text-sm font-semibold">{t(`${K}.menu`)}</p>
            <p className="text-xs text-fg-subtle">{t(`${K}.menuHint`, { count: HOME_MESSENGER_MENU_ITEMS })}</p>
          </div>
          <Controller control={control} name="home.messenger.persistent_menu" render={({ field }) => <Switch checked={field.value} onCheckedChange={field.onChange} aria-label={t(`${K}.menu`)} />} />
        </div>
        <p className="flex items-start gap-2 rounded-md bg-info-soft p-3 text-xs text-fg-muted">
          <Icon name="info" className="mt-0.5 size-3.5" />
          <span>
            {t(`${K}.note`)}{" "}
            <AppLink href={consoleRoutes.channels(summary.slug, agentId)} className="font-semibold text-primary-ink hover:underline">
              {t(`${K}.channels`)}
            </AppLink>
          </span>
        </p>
        <SyncStatus profile={profile} />
      </CardBody>
    </Card>
  );
}
